import { useEffect, useRef } from 'react';
import { formatImageDate } from '../helpers';

export const useStreetView = (map: google.maps.Map | null | undefined, setIsStreetViewEnabled?: (flag: boolean) => void) => {
    useEffect(() => {
        if (!map) return;

        const streetView = map.getStreetView();
        let dateControlDiv: HTMLDivElement | null = null;

        const createTimestampControl = () => {
            if (dateControlDiv) return;

            streetView.setOptions({ imageDateControl: true });

            dateControlDiv = document.createElement('div');
            Object.assign(dateControlDiv.style, {
                borderRadius: '2px',
                background: 'rgba(34, 34, 34, 0.70)',
                color: 'white',
                padding: '8px 12px',
                margin: '10px',
                fontSize: '12px',
                fontWeight: '500',
                boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                fontFamily: 'Roboto, Arial, sans-serif'
            });
            dateControlDiv.textContent = 'Loading...';

            streetView.controls[google.maps.ControlPosition.LEFT_TOP].push(dateControlDiv);

            const position = streetView.getPosition();
            if (position) {
                const streetViewService = new google.maps.StreetViewService();
                streetViewService.getPanorama(
                    { location: position, radius: 50 },
                    (data, status) => {
                        if (status === google.maps.StreetViewStatus.OK && data?.imageDate && dateControlDiv) {
                            dateControlDiv.textContent = formatImageDate(data.imageDate);
                        } else if (dateControlDiv) {
                            dateControlDiv.textContent = 'Image date unavailable';
                        }
                    }
                );
            }
        };

        const removeTimestampControl = () => {
            if (dateControlDiv) {
                const controls = streetView.controls[google.maps.ControlPosition.LEFT_TOP];
                const index = controls.getArray().indexOf(dateControlDiv);
                if (index > -1) {
                    controls.removeAt(index);
                }
                dateControlDiv = null;
            }
        };

        const streetViewListener = streetView.addListener("visible_changed", () => {
            if (setIsStreetViewEnabled) {
                setIsStreetViewEnabled(streetView.getVisible());
            }

            if (!streetView.getVisible() && document.fullscreenElement) {
                document.exitFullscreen();
                removeTimestampControl();
            } else if (streetView.getVisible()) {
                createTimestampControl();
            }
        });

        const positionListener = streetView.addListener("position_changed", () => {
            const position = streetView.getPosition();
            if (position && dateControlDiv) {
                const streetViewService = new google.maps.StreetViewService();
                streetViewService.getPanorama(
                    { location: position, radius: 50 },
                    (data, status) => {
                        if (status === google.maps.StreetViewStatus.OK && data?.imageDate && dateControlDiv) {
                            dateControlDiv.textContent = formatImageDate(data.imageDate);
                        } else if (dateControlDiv) {
                            dateControlDiv.textContent = 'Image date unavailable';
                        }
                    }
                );
            }
        });

        if (streetView.getVisible()) {
            createTimestampControl();
        }

        return () => {
            streetViewListener.remove();
            positionListener.remove();
            removeTimestampControl();
        };
    }, [map, setIsStreetViewEnabled]);
};
